/**
 * The last step's call line for a by-phone service (service-by-phone D5), shared by the review and
 * payment screens in the app and on the phone website: Location, By phone, "Dana Wilcher will call
 * you at (512) 555-0199." with Change. Change opens the number in place (never in a sheet); with no
 * number on file the row opens as the field and says why the bottom button waits. Saving goes through
 * the surface's `save`, which refetches the flow, so the line then names the saved number.
 */
import { useState } from 'react';
import { formatUsPhoneWithCountry } from '@/components/forms/formatPhone';
import { PHONE_NEEDED, type PhoneCallStep } from '@/pages/booking/shared/bookingRules';
import { PhoneIcon } from './confirmIcons';
import styles from './ConfirmPage.module.css';

export interface PhoneCallProps {
  readonly step: PhoneCallStep;
  /** Saves the number as the client's own, then refetches the flow; rejects when it was not saved. */
  readonly save: (phone: string) => Promise<void>;
}

/** Change is open, or the step asks for a number: the bottom button waits while either holds. */
export function usePhoneCallAsk(phoneCall: PhoneCallProps | null | undefined) {
  const [changing, setChanging] = useState(false);
  return { changing, setChanging, asking: Boolean(phoneCall && (phoneCall.step.needsNumber || changing)) };
}

export function PhoneCallRow({
  phoneCall,
  changing,
  onChangingChange,
}: {
  phoneCall: PhoneCallProps;
  changing: boolean;
  onChangingChange: (changing: boolean) => void;
}) {
  const { step, save } = phoneCall;
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const head = (
    <>
      <div className={styles.rowLabel}>Location</div>
      <div className={styles.rowValue}>By phone</div>
    </>
  );

  if (!step.needsNumber && !changing) {
    return (
      <div className={styles.row}>
        <PhoneIcon />
        <div>
          {head}
          <div className={styles.rowValue}>{step.line}</div>
        </div>
        <button
          type="button"
          className={styles.rowLink}
          onClick={() => {
            setDraft(formatUsPhoneWithCountry(step.phone ?? ''));
            setError(null);
            onChangingChange(true);
          }}
        >
          Change
        </button>
      </div>
    );
  }

  const onSave = async () => {
    const phone = draft.trim();
    if (!phone || saving) return;
    setSaving(true);
    setError(null);
    try {
      await save(phone);
      onChangingChange(false);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'The number was not saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.row}>
      <PhoneIcon />
      <div className={styles.phoneGrow}>
        {head}
        <label className={`${styles.phoneField} ${styles.phoneFieldActive}`}>
          <span className={styles.phoneFieldLabel}>Number to call</span>
          <input
            className={styles.phoneFieldBox}
            type="tel"
            inputMode="tel"
            maxLength={32}
            autoComplete="off"
            value={draft}
            onChange={(e) => setDraft(formatUsPhoneWithCountry(e.target.value))}
          />
        </label>
        {(error || step.needsNumber) && (
          <span className={`${styles.noticeText} ${styles.phoneHint}`} role={error ? 'alert' : undefined}>
            {error ?? PHONE_NEEDED}
          </span>
        )}
        <div className={styles.phoneActions}>
          <button type="button" className={styles.phoneSave} disabled={!draft.trim() || saving} onClick={() => void onSave()}>
            Save
          </button>
          {!step.needsNumber && (
            <button type="button" className={styles.phoneCancel} disabled={saving} onClick={() => onChangingChange(false)}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** The row on its own, for a screen without the review's detail rows (the payment page). */
export function PhoneCallDetails(props: Parameters<typeof PhoneCallRow>[0]) {
  return (
    <div className={styles.det}>
      <PhoneCallRow {...props} />
    </div>
  );
}
